import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import {
  fetchPricingLevels, fetchPricingApplications, approvePricingApplication, rejectPricingApplication,
} from "@admin/services/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pill } from "@admin/components/Primitives";
import { paiseFmt, paiseFmt2, customerPreview, ContributionCell } from "@admin/components/pricing/money";

export const OPEN_STATUSES = "pending,custom_review";

function ApplicationRow({ app, gstBps, customMarginPctBps, onDecided }) {
  const [level, setLevel] = useState(app.requested_level);
  const [customFee, setCustomFee] = useState(app.custom_fee_paise ? String(app.custom_fee_paise / 100) : "");
  const [margin, setMargin] = useState("");
  const [expenses, setExpenses] = useState("0");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isCustom = Number(level) === 11;
  const marginIsBlank = String(margin).trim() === "";
  const defaultMarginRupees = marginIsBlank && isCustom
    ? Math.floor((Number(customFee) || 0) * (customMarginPctBps || 0) / 100)
    : 0;
  const previewMarginRupees = marginIsBlank ? defaultMarginRupees : Math.max(0, Number(margin) || 0);
  const preview = isCustom ? customerPreview(customFee, previewMarginRupees, gstBps, expenses) : null;

  const approve = async () => {
    setBusy(true);
    try {
      const payload = { level: Number(level) };
      if (isCustom) {
        payload.custom_fee_rupees = Number(customFee);
        if (!marginIsBlank) payload.margin_rupees = Math.max(0, Number(margin) || 0);
        payload.expenses_rupees = Math.max(0, Number(expenses) || 0);
      }
      if (reason.trim()) payload.reason = reason.trim();
      const r = await approvePricingApplication(app.id, payload);
      toast.success(r.status === "adjusted" ? "Level adjusted & activated" : "Level approved & activated");
      onDecided(app.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    setBusy(false);
  };

  const reject = async () => {
    if (!reason.trim()) return toast.error("A reason is required to reject");
    setBusy(true);
    try {
      await rejectPricingApplication(app.id, { reason: reason.trim() });
      toast.success("Application rejected");
      onDecided(app.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    setBusy(false);
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4" data-testid={`pricing-app-${app.id}`}>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div>
          <p className="font-bold text-sm text-[#0A2540]">{app.lawyer_name || app.lawyer_id}</p>
          <p className="text-xs text-slate-500">
            {app.yoe} yrs exp · requested <span className="font-bold">Level {app.requested_level}</span>
            {app.custom_fee_paise ? ` (${paiseFmt(app.custom_fee_paise)})` : ""} · system recommends Level {app.recommended_level}
            · via {app.source === "onboarding" ? "onboarding" : "lawyer portal"}
          </p>
        </div>
        <Pill color={app.status === "custom_review" ? "amber" : "blue"}>
          {app.status === "custom_review" ? "Custom review" : "Pending"}
        </Pill>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-400">Approve as level</label>
          <Input type="number" min={1} max={11} value={level} onChange={(e) => setLevel(e.target.value)}
            className="w-20 mt-0.5" data-testid={`pricing-app-level-${app.id}`} />
        </div>
        {isCustom && (
          <>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400">Lawyer fee (₹)</label>
              <Input type="number" min={1} value={customFee} onChange={(e) => setCustomFee(e.target.value)}
                className="w-28 mt-0.5" data-testid={`pricing-app-customfee-${app.id}`} />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400">Advoset margin (₹)</label>
              <Input type="number" min={0} value={margin} placeholder={`${(customMarginPctBps || 0) / 100}% default`}
                onChange={(e) => setMargin(e.target.value)}
                className="w-28 mt-0.5" data-testid={`pricing-app-margin-${app.id}`} />
              {marginIsBlank && (
                <p className="text-[10px] text-slate-400 mt-0.5">Using {(customMarginPctBps || 0) / 100}% default</p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400">Other expenses (₹)</label>
              <Input type="number" min={0} value={expenses} onChange={(e) => setExpenses(e.target.value)}
                className="w-28 mt-0.5" data-testid={`pricing-app-expenses-${app.id}`} />
            </div>
          </>
        )}
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] font-bold uppercase text-slate-400">Reason (required to adjust/reject)</label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. offline evaluation recommended L3"
            className="mt-0.5" data-testid={`pricing-app-reason-${app.id}`} />
        </div>
        <Button onClick={approve} disabled={busy} data-testid={`pricing-app-approve-${app.id}`}
          className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-9">
          <Check size={14} /> {Number(level) === app.requested_level ? "Approve" : "Adjust & approve"}
        </Button>
        <Button onClick={reject} disabled={busy} variant="outline" data-testid={`pricing-app-reject-${app.id}`}
          className="rounded-lg border-rose-300 text-rose-700 font-bold h-9">
          <X size={14} /> Reject
        </Button>
      </div>
      {isCustom && Number(customFee) > 0 && (
        <p className="mt-2 text-xs text-slate-500" data-testid={`pricing-app-listing-preview-${app.id}`}>
          Customer listing: <span className="font-bold text-[#0A2540]">{paiseFmt2(preview.ex)}</span> before GST
          {" + "}{paiseFmt2(preview.gst)} GST (18%) = <span className="font-bold text-[#0A2540]">{paiseFmt2(preview.inc)}</span> payable
          {" · Est. contribution: "}<ContributionCell paise={preview.contribution} testId={`pricing-app-contribution-${app.id}`} />
        </p>
      )}
    </div>
  );
}

export default function LevelApplicationsQueue({ onCountChange }) {
  const [apps, setApps] = useState(null);
  const [gstBps, setGstBps] = useState(1800);
  const [customMarginPctBps, setCustomMarginPctBps] = useState(0);

  const load = async () => {
    try {
      const [c, a] = await Promise.all([fetchPricingLevels(), fetchPricingApplications(OPEN_STATUSES)]);
      setGstBps(c.gst_rate_bps || 1800);
      setCustomMarginPctBps(c.custom_margin_pct_bps || 0);
      setApps(a.items || []);
      onCountChange?.((a.items || []).length);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load level requests"); setApps([]); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (apps === null) return <p className="text-sm text-slate-400" data-testid="pricing-apps-loading">Loading…</p>;
  if (apps.length === 0) {
    return <p className="text-sm text-slate-400" data-testid="pricing-apps-empty">No pending level requests. Lawyers who apply for a level, or request a change from their portal, appear here.</p>;
  }
  return (
    <div className="space-y-3" data-testid="pricing-apps-list">
      {apps.map((a) => (
        <ApplicationRow key={a.id} app={a} gstBps={gstBps} customMarginPctBps={customMarginPctBps}
          onDecided={(id) => {
            const next = apps.filter((x) => x.id !== id);
            setApps(next);
            onCountChange?.(next.length);
          }} />
      ))}
    </div>
  );
}
